import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { ColumnArrow } from '../../../ColumnArrow';
import './LegendModal.css';
import { PrimaryButton } from '../../../PrimaryButton';
import { SearchBar } from '../../../SearchBar';
import { LegendModalRow } from '../LegendModalRow';
import { Modal } from '../../../Modal';

export class LegendModal extends Component {
  state = {
    filteredDataPointList: [],
    filters: { searchValue: '', sortValue: 'name', isReversed: false },
    modalAvailableColors: this.props.availableSuiteLineColors,
    modalLineColorObj: this.props.suiteLineColors,
  };

  componentDidMount() {
    this.updateFilteredDataPointList();
  }

  handleModalClose = () => {
    this.props.onRequestClose();
  };

  handleCheckboxChange = (id) => {
    let tmpLineColorObj = { ...this.state.modalLineColorObj };
    const tmpAvailableColors = [...this.state.modalAvailableColors];

    if (!tmpLineColorObj[id]) {
      // checking a new register
      const pair = { [id]: tmpAvailableColors.shift() };
      tmpLineColorObj = { ...tmpLineColorObj, ...pair };
    } else {
      // unchecking a previously selected register
      tmpAvailableColors.push(tmpLineColorObj[id]);
      delete tmpLineColorObj[id];
    }

    this.setState((state) => ({
      ...state,
      modalLineColorObj: tmpLineColorObj,
      modalAvailableColors: tmpAvailableColors,
    }));
  };

  updateSearchValue = (event) => {
    const filterList = this.state.filters;
    filterList.searchValue = event.target.value;

    this.setState((state) => ({
      ...state,
      filters: filterList,
    }));
    this.updateFilteredDataPointList();
  };

  updateSortValue = (sortValue) => {
    const isReversed = sortValue === this.state.filters.sortValue && !this.state.filters.isReversed;
    this.setState(
      (state) => ({
        ...state,
        filters: {
          ...state.filters,
          isReversed,
          sortValue,
        },
      }),
      () => this.updateFilteredDataPointList(),
    );
  };

  updateFilteredDataPointList = () => {
    const { filters, modalLineColorObj } = this.state;
    const { allSuiteDataPoints } = this.props;
    let tempDataPointList = [...allSuiteDataPoints];

    let unsortedList = [];
    const filterList = { ...filters };
    const { searchValue } = filters;

    if (filters.searchValue !== '') {
      tempDataPointList = tempDataPointList.filter((item) =>
        item.name
          .replace(/\s/g, '')
          .toLowerCase()
          .includes(`${searchValue.replace(/\s/g, '').toLowerCase()}`),
      );
    }

    const { sortValue, isReversed } = filters;

    switch (sortValue) {
      case 'selected':
        unsortedList = tempDataPointList;
        // if this is the first time sorting, or if the previous sort is not by selected
        if (!isReversed) {
          tempDataPointList = unsortedList.filter((item) => modalLineColorObj[item.id] !== undefined);
          tempDataPointList = tempDataPointList.concat(
            unsortedList.filter((item) => modalLineColorObj[item.id] === undefined),
          );
          break;
        }
        // previous sort is also by selected, we will sort in reverse
        tempDataPointList = unsortedList.filter((item) => modalLineColorObj[item.id] === undefined);
        tempDataPointList = tempDataPointList.concat(
          unsortedList.filter((item) => modalLineColorObj[item.id] !== undefined),
        );
        break;
      case 'name':
        // if this is the first time sorting, or if the previous sort is not by name
        if (!isReversed) {
          tempDataPointList = tempDataPointList.sort((a, b) => {
            if (a.name < b.name) return -1;
            if (a.name > b.name) return 1;
            return 0;
          });
          break;
        }
        // previous sort is also by name, we will sort in reverse
        tempDataPointList = tempDataPointList.sort((a, b) => {
          if (a.name > b.name) return -1;
          if (a.name < b.name) return 1;
          return 0;
        });
        break;

      // no default
    }

    this.setState((state) => ({
      ...state,
      filters: filterList,
      filteredDataPointList: tempDataPointList,
    }));
  };

  clearAll = () => {
    const defaultAvailableColors = ['line0', 'line1', 'line2', 'line3', 'line4', 'line5', 'line6'];

    this.setState((state) => ({
      ...state,
      modalLineColorObj: {},
      modalAvailableColors: defaultAvailableColors,
    }));

    this.renderModalItems();
  };

  renderModalItems() {
    const { allSuiteDataPoints } = this.props;
    const { modalLineColorObj } = this.state;

    // initially the filteredDataPointList is empty
    const modalDataPointList =
      this.state.filteredDataPointList.length === 0 && !this.state.filters.searchValue
        ? allSuiteDataPoints
        : this.state.filteredDataPointList;

    return (
      <div className="legend-modal__items">
        {modalDataPointList.map((dataPoint, index) => {
          const { id = '', name = '', status = '' } = dataPoint;
          const isSelected = modalLineColorObj[dataPoint.id] !== undefined;
          const isDisabled = Object.keys(modalLineColorObj).length >= 7 && !isSelected;
          const lineStyle = `line ${modalLineColorObj[dataPoint.id]}`;
          const key = `${index}-${id}`;
          return (
            <LegendModalRow
              key={key}
              isDisabled={isDisabled}
              isSelected={isSelected}
              lineStyle={lineStyle}
              id={id}
              name={name}
              status={status}
              onClick={this.handleCheckboxChange}
            />
          );
        })}
      </div>
    );
  }

  render() {
    const { modalLineColorObj = {}, modalAvailableColors = [] } = this.state;
    const { sortValue, isReversed } = this.state.filters;
    const modalColumnClass = 'legend-modal__column-name';
    const wideColumnClass = ' legend-modal__column-name--wide';
    const numSelectedLines = Object.keys(modalLineColorObj).length;
    const isMaxSelected = numSelectedLines === 7;

    return (
      <Modal>
        <div className="legend-modal-overlay">
          <div className="legend-modal">
            <div className="legend-modal__header">
              <span className="legend-modal__header-item legend-modal__header-item--left">
                <button className="legend-modal__clear-all" onClick={this.clearAll}>
                  Clear All
                </button>
              </span>
              <span className="legend-modal__header-item legend-modal__header-item--center legend-modal__title">
                <span className="legend-modal__title--margin">Select data sets</span>
                <span className={isMaxSelected ? 'legend-modal__title--red' : ''}>{` (${numSelectedLines} of 7)`}</span>
              </span>
              <span className="legend-modal__header-item legend-modal__header-item--right">
                <input className="legend-modal__close-button" type="button" onClick={() => this.handleModalClose()} />
              </span>
            </div>
            <div className="legend-modal__filters">
              <SearchBar
                value={this.state.filters.searchValue}
                placeholderText="Search for a register or data set"
                onChange={this.updateSearchValue}
              />
            </div>
            <div className="legend-modal__column-name-wrapper">
              <button
                className={
                  sortValue === 'selected'
                    ? `${modalColumnClass} legend-modal__column-name--selected`
                    : modalColumnClass
                }
                onClick={() => this.updateSortValue('selected')}
              >
                <span className="legend-modal__column-name-text">SELECTED</span>
                <ColumnArrow value="selected" sortValue={sortValue} isReversed={isReversed} />
              </button>
              <span className="legend-modal__column-name legend-modal__column-name--desktop">LINE STYLE</span>
              <button
                className={
                  sortValue === 'name'
                    ? `${modalColumnClass} legend-modal__column-name--selected ${wideColumnClass}`
                    : `${modalColumnClass} ${wideColumnClass}`
                }
                onClick={() => this.updateSortValue('name')}
              >
                <span className="legend-modal__column-name-text">DATA SET</span>
                <ColumnArrow value="name" sortValue={sortValue} isReversed={isReversed} />
              </button>
              <span className="legend-modal__column-name legend-modal__column-name--desktop">STATUS</span>
            </div>
            {this.renderModalItems()}
            <div className="legend-modal__linebreak" />
            <div className="legend-modal__buttons">
              <button
                className="legend-modal__button legend-modal__button-cancel"
                onClick={() => this.handleModalClose()}
              >
                <div className="legend-modal__button-cancel-text">Cancel</div>
              </button>
              <div className="legend-modal__button">
                <PrimaryButton
                  icon="save-icon"
                  label="Save Changes"
                  isDisabled={Object.keys(this.state.modalLineColorObj).length === 0}
                  onClick={() => this.props.saveChanges(modalLineColorObj, modalAvailableColors)}
                  fullWidth={true}
                />
              </div>
            </div>
          </div>
        </div>
      </Modal>
    );
  }
}

LegendModal.propTypes = {
  availableSuiteLineColors: PropTypes.arrayOf(PropTypes.string),
  onRequestClose: PropTypes.func.isRequired,
  allSuiteDataPoints: PropTypes.arrayOf(
    PropTypes.shape({
      api_location: PropTypes.string,
      id: PropTypes.string,
      name: PropTypes.string,
      status: PropTypes.string,
    }),
  ),
  saveChanges: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  suiteLineColors: PropTypes.object,
};

LegendModal.defaultProps = {
  allSuiteDataPoints: [],
  availableSuiteLineColors: [],
  suiteLineColors: {},
};

export function mapStateToProps(state) {
  return {
    allSuiteDataPoints: state.suiteReducer.allSuiteDataPoints,
    availableSuiteLineColors: state.suiteReducer.availableSuiteLineColors,
    suiteLineColors: state.suiteReducer.suiteLineColors,
  };
}

export const ConnectedLegendModal = connect(mapStateToProps)(LegendModal);
